/** Shared planogram geometry and styling, used by the interactive editor
 *  (PlanogramEditor) and the read-only heatmap view (PlanogramHeatmap).
 *  Plan coordinates are metres, origin top-left, y down; camera rotation is
 *  degrees, 0 = towards +x, clockwise — SVG's own angle convention. */

import type { CameraMarker, Fixture, FixtureKind, Product, Shelf, Wall } from "../types";

export const KINDS: { kind: FixtureKind; label: string; color: string }[] = [
  { kind: "shelf", label: "Shelf", color: "#4f8ef7" },
  { kind: "counter", label: "Counter", color: "#a56ef5" },
  { kind: "fridge", label: "Fridge", color: "#2fbdc9" },
  { kind: "entrance", label: "Entrance", color: "#35b26a" },
  { kind: "zone", label: "Zone", color: "#8a8f98" },
];

export const KIND_COLOR = Object.fromEntries(
  KINDS.map((k) => [k.kind, k.color]),
) as Record<FixtureKind, string>;

/** Metres of canvas around the store outline (labels, handles). */
export const PLAN_MARGIN = 0.6;

export const DEG = Math.PI / 180;

/** The view-cone wedge of a camera as an SVG path ("" for a full circle —
 *  draw a <circle> instead). */
export function conePath(cam: CameraMarker): string {
  if (cam.fov >= 360) return "";
  const a0 = (cam.rotation - cam.fov / 2) * DEG;
  const a1 = (cam.rotation + cam.fov / 2) * DEG;
  const p0 = [cam.x + Math.cos(a0) * cam.range, cam.y + Math.sin(a0) * cam.range];
  const p1 = [cam.x + Math.cos(a1) * cam.range, cam.y + Math.sin(a1) * cam.range];
  const largeArc = cam.fov > 180 ? 1 : 0;
  return (
    `M ${cam.x} ${cam.y} L ${p0[0]} ${p0[1]} ` +
    `A ${cam.range} ${cam.range} 0 ${largeArc} 1 ${p1[0]} ${p1[1]} Z`
  );
}

/** Whether the point (px, py) lies inside a camera's view cone (within range
 *  and, unless the camera sees all round, within ±fov/2 of its aim). */
export function coneContains(cam: CameraMarker, px: number, py: number): boolean {
  const dx = px - cam.x;
  const dy = py - cam.y;
  if (dx * dx + dy * dy > cam.range * cam.range) return false;
  if (cam.fov >= 360) return true;
  let delta = Math.atan2(dy, dx) / DEG - cam.rotation;
  delta = ((((delta + 180) % 360) + 360) % 360) - 180;
  return Math.abs(delta) <= cam.fov / 2;
}

// --- Camera visibility (occlusion) -------------------------------------------
//
// A camera's drawn cone is only its mount, aim, FOV and range — walls and tall
// fixtures cut what it actually sees. Everything here works on sight-blocking
// segments in plan metres: every wall, plus the four footprint edges of each
// fixture that blocks the view. The same geometry clips the rendered cones
// (viewPath) and gates the heatmap grid (visibleFrom via heatCells), so the
// map never paints heat on floor the camera cannot physically see.

export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Racks at or above this height (metres) block a ceiling camera's view of
 *  the floor behind them; lower ones are assumed seen over. */
export const SEE_OVER_HEIGHT = 1.4;

/** Whether a fixture cuts camera views: the operator's explicit choice when
 *  set, else the designed rack height against SEE_OVER_HEIGHT, else by kind —
 *  shelf runs and fridges block, counters/entrances/zones are seen over. */
export function fixtureBlocksView(f: Fixture): boolean {
  if (f.blocks_view !== null && f.blocks_view !== undefined) return f.blocks_view;
  if (f.height !== null && f.height !== undefined) return f.height >= SEE_OVER_HEIGHT;
  return f.kind === "shelf" || f.kind === "fridge";
}

/** The sight-blocking segments one camera is subject to. A camera whose mount
 *  sits over a blocking fixture sees out of it (its own box is skipped),
 *  otherwise it would be blind in every direction. */
export function cameraBlockers(
  cam: CameraMarker,
  walls: Wall[],
  fixtures: Fixture[],
): Segment[] {
  const segments: Segment[] = walls.map((w) => ({
    x1: w.x1,
    y1: w.y1,
    x2: w.x2,
    y2: w.y2,
  }));
  for (const f of fixtures) {
    if (!fixtureBlocksView(f)) continue;
    if (cam.x >= f.x && cam.x <= f.x + f.w && cam.y >= f.y && cam.y <= f.y + f.h) {
      continue;
    }
    const x2 = f.x + f.w;
    const y2 = f.y + f.h;
    segments.push(
      { x1: f.x, y1: f.y, x2, y2: f.y },
      { x1: x2, y1: f.y, x2, y2 },
      { x1: x2, y1: y2, x2: f.x, y2 },
      { x1: f.x, y1: y2, x2: f.x, y2: f.y },
    );
  }
  return segments;
}

/** How far a ray from the camera along `theta` (radians) travels before a
 *  blocking segment stops it, capped at the camera's range. */
function castRay(cam: CameraMarker, theta: number, blockers: Segment[]): number {
  const dx = Math.cos(theta);
  const dy = Math.sin(theta);
  let dist = cam.range;
  for (const s of blockers) {
    const sx = s.x2 - s.x1;
    const sy = s.y2 - s.y1;
    const denom = dx * sy - dy * sx;
    if (Math.abs(denom) < 1e-12) continue;
    const t = ((s.x1 - cam.x) * sy - (s.y1 - cam.y) * sx) / denom;
    const u = ((s.x1 - cam.x) * dy - (s.y1 - cam.y) * dx) / denom;
    if (t > 1e-6 && u >= -1e-6 && u <= 1 + 1e-6 && t < dist) dist = t;
  }
  return dist;
}

/** Whether the camera actually sees the point: inside its cone AND no wall or
 *  blocking fixture crosses the sight line. */
export function visibleFrom(
  cam: CameraMarker,
  px: number,
  py: number,
  blockers: Segment[],
): boolean {
  if (!coneContains(cam, px, py)) return false;
  const dist = Math.hypot(px - cam.x, py - cam.y);
  if (dist < 1e-9) return true;
  return castRay(cam, Math.atan2(py - cam.y, px - cam.x), blockers) >= dist - 1e-6;
}

/** Angular resolution of the clipped-view outline, degrees per sample. */
const VIEW_STEP = 2;

/** The region a camera actually sees as an SVG path: the view cone ray-cast
 *  against its blockers (rays at a fixed angular step plus each segment
 *  endpoint ± a hair, so corners stay crisp). Replaces conePath wherever the
 *  plan's occlusion should show; "" when the view is degenerate. */
export function viewPath(cam: CameraMarker, blockers: Segment[]): string {
  const full = cam.fov >= 360;
  const fov = full ? 360 : cam.fov;
  const start = full ? 0 : cam.rotation - cam.fov / 2;
  const offsets: number[] = [];
  for (let a = 0; a < fov; a += VIEW_STEP) offsets.push(a);
  offsets.push(fov);
  for (const s of blockers) {
    for (const [ex, ey] of [
      [s.x1, s.y1],
      [s.x2, s.y2],
    ] as const) {
      const angle = Math.atan2(ey - cam.y, ex - cam.x) / DEG;
      for (const jitter of [-0.4, 0, 0.4]) {
        const offset = (((angle + jitter - start) % 360) + 360) % 360;
        if (offset <= fov) offsets.push(offset);
      }
    }
  }
  offsets.sort((a, b) => a - b);
  const points: string[] = [];
  let previous = -1;
  for (const offset of offsets) {
    if (offset - previous < 1e-4) continue;
    previous = offset;
    const theta = (start + offset) * DEG;
    const d = castRay(cam, theta, blockers);
    const px = cam.x + Math.cos(theta) * d;
    const py = cam.y + Math.sin(theta) * d;
    points.push(`${px.toFixed(3)} ${py.toFixed(3)}`);
  }
  if (points.length < 2) return "";
  return full
    ? `M ${points[0]} L ${points.slice(1).join(" L ")} Z`
    : `M ${cam.x} ${cam.y} L ${points.join(" L ")} Z`;
}

/** Side of one heat cell on the floor plan, in metres. */
export const HEAT_CELL = 0.5;

export interface HeatCell {
  x: number;
  y: number;
  /** Hottest metric value of the cameras that see the cell centre, or null
   *  when only cameras without data in the window see it. */
  value: number | null;
}

/** The floor-plan cells at least one camera actually sees — inside a cone AND
 *  not cut off by a wall or blocking fixture (fixtureBlocksView). The heat is
 *  projected onto the floor grid (the hottest seeing camera wins where views
 *  overlap), so the map reads without drawing the cones themselves; floor no
 *  camera sees stays bare, never falsely warm or cold. `values` maps camera
 *  id → metric value; cameras absent from it count as seeing the cell with no
 *  data. */
export function heatCells(
  plan: { width: number; height: number; cameras: CameraMarker[]; walls: Wall[]; fixtures: Fixture[] },
  values: Record<string, number>,
  cell = HEAT_CELL,
): HeatCell[] {
  const { width, height, cameras, walls, fixtures } = plan;
  const cells: HeatCell[] = [];
  if (cameras.length === 0) return cells;
  const blockers = cameras.map((cam) => cameraBlockers(cam, walls ?? [], fixtures));
  const cols = Math.ceil(width / cell);
  const rows = Math.ceil(height / cell);
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const cx = (i + 0.5) * cell;
      const cy = (j + 0.5) * cell;
      let covered = false;
      let value: number | null = null;
      for (let c = 0; c < cameras.length; c++) {
        if (!visibleFrom(cameras[c], cx, cy, blockers[c])) continue;
        covered = true;
        const v = values[cameras[c].id];
        if (v !== undefined && (value === null || v > value)) value = v;
      }
      if (covered) cells.push({ x: i * cell, y: j * cell, value });
    }
  }
  return cells;
}

const HEAT_STOPS: [number, number, number][] = [
  [59, 130, 246], // cool — blue
  [245, 158, 11], // busy — amber
  [239, 68, 68], // hot — red
];

/** Sequential cool→hot ramp for t in [0, 1]. */
export function heatColor(t: number): string {
  const x = Math.min(Math.max(t, 0), 1) * (HEAT_STOPS.length - 1);
  const i = Math.min(Math.floor(x), HEAT_STOPS.length - 2);
  const f = x - i;
  const [r0, g0, b0] = HEAT_STOPS[i];
  const [r1, g1, b1] = HEAT_STOPS[i + 1];
  const mix = (a: number, b: number) => Math.round(a + (b - a) * f);
  return `rgb(${mix(r0, r1)}, ${mix(g0, g1)}, ${mix(b0, b1)})`;
}

// --- Rack (shelf design) -----------------------------------------------------

/** Fixture kinds that have a rack to design: things with shelf levels that
 *  hold product. Entrances and analysis zones do not. */
export const RACK_KINDS: readonly FixtureKind[] = ["shelf", "fridge", "counter"];

export const hasRack = (f: Fixture) => RACK_KINDS.includes(f.kind);

/** Rack height assumed until the operator sets one (metres). */
export const DEFAULT_RACK_HEIGHT = 1.8;
export const MAX_RACK_HEIGHT = 10;
/** Drawn thickness of a shelf board (metres) — also the gap products on the
 *  shelf below lose to it. */
export const SHELF_THICKNESS = 0.03;

/** The rack's front width and depth, in metres. The longer side of a
 *  fixture's plan footprint is its front (a shelf run faces the aisle along
 *  its length); the shorter side is how deep the shelves are. */
export function rackSize(f: Fixture): { front: number; depth: number } {
  return { front: Math.max(f.w, f.h), depth: Math.min(f.w, f.h) };
}

/** Total width of a product run (all facings), metres. */
export const productWidth = (p: Product) => p.w * p.facings;

/** Shelves bottom-up (lowest elevation first). */
export const shelvesByElevation = (shelves: Shelf[]) =>
  [...shelves].sort((a, b) => a.elevation - b.elevation);

/** Free height above a shelf's surface: up to the underside of the next
 *  board, or to the top of the rack. */
export function shelfClearance(shelf: Shelf, shelves: Shelf[], rackHeight: number): number {
  const above = shelves
    .filter((s) => s.id !== shelf.id && s.elevation > shelf.elevation)
    .reduce((min, s) => Math.min(min, s.elevation), Infinity);
  return (above === Infinity ? rackHeight : above - SHELF_THICKNESS) - shelf.elevation;
}

export interface PlacementIssue {
  /** The run extends past the rack's right edge. */
  overflow: boolean;
  /** Taller than the gap up to the shelf above. */
  tooTall: boolean;
  /** Ids of runs on the same shelf it overlaps with. */
  overlaps: string[];
}

/** Why a product run does not fit where it is — used to flag it in the
 *  editor without ever blocking the experiment. */
export function placementIssues(
  product: Product,
  shelf: Shelf,
  shelves: Shelf[],
  front: number,
  rackHeight: number,
): PlacementIssue {
  const x0 = product.x;
  const x1 = product.x + productWidth(product);
  const eps = 1e-6;
  return {
    overflow: x1 > front + eps,
    tooTall: product.h > shelfClearance(shelf, shelves, rackHeight) + eps,
    overlaps: shelf.products
      .filter((o) => o.id !== product.id && o.x < x1 - eps && o.x + productWidth(o) > x0 + eps)
      .map((o) => o.id),
  };
}

/** Fraction of a shelf's front width covered by product (can exceed 1). */
export function shelfFill(shelf: Shelf, front: number): number {
  const used = shelf.products.reduce((sum, p) => sum + productWidth(p), 0);
  return front > 0 ? used / front : 0;
}

/** Product display colours, cycled for new products. */
export const PRODUCT_COLORS = [
  "#4f8ef7",
  "#35b26a",
  "#f59e0b",
  "#ef6b6b",
  "#a56ef5",
  "#2fbdc9",
  "#d9538f",
  "#8a8f98",
];
